import { DragEvent, FC, Key, ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { Button, Flex, Upload, UploadProps } from 'antd';
import { TbFolderUp, TbPaperclip } from 'react-icons/tb';
import { toast } from '@/shared/components/toasts/PlatformToast';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import {
    AttachmentDestination,
    CloudAttachment,
    CloudStorageConnection,
    getCloudStorageCapabilities,
} from '@/services';
import { DropPlaceholder } from '@/shared/components/drag-and-drop';
import { setNativeDragPreview } from '@/shared/components/drag-and-drop/nativeDragPreview';
import { getDropZoneIdleStyle } from '@/shared/components/drag-and-drop/platformStyles';
import useNativeDropZone from '@/shared/components/drag-and-drop/useNativeDropZone';
import { EmptyView } from '@/shared/components/layouts/views/EmptySection';
import CompactVirtualizedList from '@/shared/components/lists/CompactVirtualizedList';
import { BaseSkeleton } from '@/shared/components/skeletons';

import { ACCEPTED_ATTACHMENT_EXTENSIONS, MAX_ATTACHMENT_BATCH_FILES } from '../StagedAttachmentDropzone';
import { buildAttachmentTree } from './attachmentTree';
import AttachmentFileListItem from './components/AttachmentFileListItem';
import AttachmentFolderSection from './components/AttachmentFolderSection';
import AttachmentUploadQueue from './components/AttachmentUploadQueue';
import { AttachmentUploadBatchProgress, useAttachmentBatchUpload } from './useAttachmentBatchUpload';
import useAttachmentFilePresentation, { formatAttachmentFileSize } from './useAttachmentFilePresentation';

const { Dragger } = Upload;

interface AttachmentFileListProps {
    bulkActions?: ReactNode;
    canUpload: boolean;
    connection: CloudStorageConnection;
    destination: AttachmentDestination;
    files: CloudAttachment[];
    filterActive?: boolean;
    height: string;
    loading: boolean;
    movingAttachmentIds?: ReadonlySet<string>;
    syncingFolderIds?: ReadonlySet<string>;
    onMoveAttachment?: (sourceAttachmentId: string, destinationFolderId?: string) => Promise<void> | void;
    onSelectedKeysChange: (selectedKeys: ReadonlySet<Key>) => void;
    onUploaded: (attachment: CloudAttachment) => void;
    onOpen?: (attachment: CloudAttachment) => void;
    resourceLabel: string;
    selectedKeys: ReadonlySet<Key>;
}

const AttachmentFileList: FC<AttachmentFileListProps> = ({
    canUpload,
    bulkActions,
    connection,
    destination,
    files,
    filterActive = false,
    height,
    loading,
    movingAttachmentIds = new Set(),
    syncingFolderIds = new Set(),
    onMoveAttachment,
    onSelectedKeysChange,
    onUploaded,
    onOpen,
    resourceLabel,
    selectedKeys,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const draggedAttachmentIdRef = useRef<string>();
    const [draggedAttachmentId, setDraggedAttachmentId] = useState<string>();
    const [draggedAttachmentHeight, setDraggedAttachmentHeight] = useState<number>();
    const [dropTargetFolderId, setDropTargetFolderId] = useState<string | null>();
    const capabilities = getCloudStorageCapabilities(connection);
    const handleBatchProgress = useCallback((progress: AttachmentUploadBatchProgress) => {
        const toastId = `attachment-upload-${progress.batchId}`;
        const description = `${progress.completed.toLocaleString()} of ${progress.total.toLocaleString()} uploaded`;

        if (!progress.done) {
            toast.loading('Uploading attachments', { description, id: toastId });
            return;
        }

        if (progress.failed || progress.canceled) {
            toast.warning('Attachment upload completed', { description, id: toastId });
            return;
        }

        toast.success(progress.total === 1 ? 'Attachment uploaded' : 'Attachments uploaded', {
            description,
            id: toastId,
        });
    }, []);
    const {
        cancel,
        customRequest,
        items: uploadItems,
        retry,
    } = useAttachmentBatchUpload({
        connection,
        destination,
        onBatchProgress: handleBatchProgress,
        onUploaded,
    });

    const filePresentation = useAttachmentFilePresentation();
    const attachmentTree = useMemo(() => buildAttachmentTree(files), [files]);

    const getDraggedAttachment = useCallback(
        (event: DragEvent<HTMLElement>) => {
            const id =
                draggedAttachmentIdRef.current ||
                draggedAttachmentId ||
                event.dataTransfer.getData('application/x-countercyclical-attachment-id');

            return files.find((attachment) => attachment.id === id);
        },
        [draggedAttachmentId, files],
    );

    const canMoveAttachment = useCallback((source?: CloudAttachment, destinationFolder?: CloudAttachment) => {
        if (!source || source.id === destinationFolder?.id) {
            return false;
        }

        const sourcePath = source.relativePath ?? source.name;
        const destinationPath = destinationFolder?.relativePath;
        const movedPath = [destinationPath, source.name].filter(Boolean).join('/');

        return movedPath !== sourcePath && (source.kind !== 'folder' || !destinationPath?.startsWith(`${sourcePath}/`));
    }, []);

    const handleAttachmentDragStart = useCallback(
        (event: DragEvent<HTMLElement>, attachment: CloudAttachment) => {
            if (movingAttachmentIds.has(attachment.id) || !onMoveAttachment) {
                event.preventDefault();
                return;
            }

            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('application/x-countercyclical-attachment-id', attachment.id);
            event.dataTransfer.setData('text/plain', attachment.name);
            const previewSource = event.currentTarget.closest<HTMLElement>('[data-attachment-drag-preview-source]');

            if (previewSource) {
                setDraggedAttachmentHeight(setNativeDragPreview(event, previewSource, token).height);
            }

            draggedAttachmentIdRef.current = attachment.id;
            setDraggedAttachmentId(attachment.id);
            setDropTargetFolderId(undefined);
        },
        [movingAttachmentIds, onMoveAttachment, token],
    );

    const resetAttachmentDrag = useCallback(() => {
        draggedAttachmentIdRef.current = undefined;
        setDraggedAttachmentId(undefined);
        setDraggedAttachmentHeight(undefined);
        setDropTargetFolderId(undefined);
    }, []);

    const canDropAttachment = useCallback(
        (event: DragEvent<HTMLElement>, destinationFolder?: CloudAttachment) => {
            const source = getDraggedAttachment(event);

            return canMoveAttachment(source, destinationFolder);
        },
        [canMoveAttachment, getDraggedAttachment],
    );

    const handleDropTargetChange = useCallback((targetFolderId: string | null, active: boolean) => {
        setDropTargetFolderId((currentTargetFolderId) => {
            if (active) {
                return targetFolderId;
            }

            return currentTargetFolderId === targetFolderId ? undefined : currentTargetFolderId;
        });
    }, []);

    const handleAttachmentDrop = useCallback(
        (event: DragEvent<HTMLElement>, destinationFolder?: CloudAttachment) => {
            const source = getDraggedAttachment(event);

            if (!canMoveAttachment(source, destinationFolder) || !source || !onMoveAttachment) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
            resetAttachmentDrag();
            void Promise.resolve(onMoveAttachment(source.id, destinationFolder?.id));
        },
        [canMoveAttachment, getDraggedAttachment, onMoveAttachment, resetAttachmentDrag],
    );

    const beforeUpload: NonNullable<UploadProps['beforeUpload']> = (file, selectedFiles) => {
        if (selectedFiles.length > MAX_ATTACHMENT_BATCH_FILES) {
            toast.error('Too many attachments', {
                description: `Select up to ${MAX_ATTACHMENT_BATCH_FILES} files at a time.`,
            });
            return Upload.LIST_IGNORE;
        }

        if (file.size > capabilities.maxFileSizeBytes) {
            toast.error('Attachment is too large', {
                description: `${file.name} exceeds the ${formatAttachmentFileSize(capabilities.maxFileSizeBytes)} limit for ${connection.type}.`,
            });
            return Upload.LIST_IGNORE;
        }

        return true;
    };

    const uploadProps: UploadProps = {
        accept: ACCEPTED_ATTACHMENT_EXTENSIONS,
        beforeUpload,
        customRequest,
        disabled: !canUpload,
        multiple: true,
        showUploadList: false,
    };

    const renderFileList = useCallback(
        (visibleFiles: CloudAttachment[]) => {
            if (!visibleFiles.length) {
                return null;
            }

            return (
                <CompactVirtualizedList
                    dataSource={visibleFiles}
                    dismissSelectionOnOutside={false}
                    getItemKey={(file) => file.id}
                    selectable
                    selectedKeys={selectedKeys}
                    selectionLabel='Attachments'
                    showHeader={false}
                    title='Attachments'
                    onSelectedKeysChange={onSelectedKeysChange}
                    renderItem={(file: CloudAttachment) => {
                        const isDragging = draggedAttachmentId === file.id;
                        const isMoving = movingAttachmentIds.has(file.id);

                        return (
                            <AttachmentFileListItem
                                key={file.id}
                                file={file}
                                isDragging={isDragging}
                                isMoving={isMoving}
                                onDragEnd={resetAttachmentDrag}
                                onDragStart={handleAttachmentDragStart}
                                onMoveEnabled={Boolean(onMoveAttachment)}
                                onOpen={onOpen}
                                presentation={filePresentation[file.mimeType]}
                            />
                        );
                    }}
                />
            );
        },
        [
            draggedAttachmentId,
            filePresentation,
            handleAttachmentDragStart,
            movingAttachmentIds,
            onMoveAttachment,
            onOpen,
            onSelectedKeysChange,
            resetAttachmentDrag,
            selectedKeys,
        ],
    );

    const draggedAttachment = files.find((attachment) => attachment.id === draggedAttachmentId);
    const showRootDropTarget = Boolean(
        draggedAttachment && (draggedAttachment.relativePath ?? draggedAttachment.name) !== draggedAttachment.name,
    );
    const rootDropActive = dropTargetFolderId === null;
    const canDropOnRoot = useCallback(
        (event: DragEvent<HTMLDivElement>) => canDropAttachment(event),
        [canDropAttachment],
    );
    const handleRootDrop = useCallback(
        (event: DragEvent<HTMLDivElement>) => handleAttachmentDrop(event),
        [handleAttachmentDrop],
    );
    const handleRootDropActiveChange = useCallback(
        (active: boolean) => handleDropTargetChange(null, active),
        [handleDropTargetChange],
    );
    const rootDropZoneHandlers = useNativeDropZone<HTMLDivElement>({
        active: rootDropActive,
        canDrop: canDropOnRoot,
        disabled: !showRootDropTarget,
        onActiveChange: handleRootDropActiveChange,
        onDrop: handleRootDrop,
        stopPropagation: false,
    });
    const rootDropZoneBaseStyle = {
        borderRadius: token.borderRadiusLG,
        minHeight: '100%',
    };
    const rootDropZoneStyle = getDropZoneIdleStyle(rootDropZoneBaseStyle, token);

    const renderAttachmentTree = () => (
        <div {...rootDropZoneHandlers} aria-label='Attachment root' style={rootDropZoneStyle}>
            {renderFileList(attachmentTree.files)}
            {rootDropActive ? (
                <DropPlaceholder
                    data-attachment-drop-placeholder='true'
                    style={{ height: draggedAttachmentHeight ?? '3.75rem', margin: '0.25rem 0' }}
                />
            ) : null}
            {Array.from(attachmentTree.folders.values()).map((folder) => (
                <AttachmentFolderSection
                    key={folder.key}
                    draggedAttachmentId={draggedAttachmentId}
                    draggedAttachmentHeight={draggedAttachmentHeight}
                    dropTargetFolderId={dropTargetFolderId}
                    folder={folder}
                    movingAttachmentIds={movingAttachmentIds}
                    syncingFolderIds={syncingFolderIds}
                    renderFiles={renderFileList}
                    onDragEnd={resetAttachmentDrag}
                    onDragStart={handleAttachmentDragStart}
                    canDropInFolder={canDropAttachment}
                    onDropFolder={handleAttachmentDrop}
                    onDropTargetChange={handleDropTargetChange}
                    onSelectedKeysChange={onSelectedKeysChange}
                    selectedKeys={selectedKeys}
                />
            ))}
        </div>
    );

    const renderUploadControls = (empty = false) => (
        <div
            style={{
                boxSizing: 'border-box',
                height: empty ? '100%' : undefined,
                minHeight: 0,
                padding: empty ? '1rem' : '0.75rem 1rem',
            }}
        >
            <Dragger
                {...uploadProps}
                style={{ height: empty ? '100%' : undefined, padding: empty ? '1rem' : '0.75rem' }}
            >
                <EmptyView
                    description='Drag and drop multiple files, or click anywhere in this area to browse.'
                    extra={
                        <>
                            <div onClick={(event) => event.stopPropagation()}>
                                <Upload {...uploadProps} directory disabled={!capabilities.supportsFolders}>
                                    <Button
                                        icon={<TbFolderUp style={{ color: token.colorTextTertiary }} />}
                                        size='small'
                                    >
                                        Choose Folder
                                    </Button>
                                </Upload>
                            </div>
                            <small style={{ color: token.colorTextTertiary, display: 'block', marginTop: '0.375rem' }}>
                                Up to 100 files per selection and{' '}
                                {formatAttachmentFileSize(capabilities.maxFileSizeBytes)} per file for this{' '}
                                {resourceLabel.toLowerCase()}.
                            </small>
                        </>
                    }
                    extraStyle={{ marginTop: '0.5rem' }}
                    height={empty ? '100%' : 'auto'}
                    icon={<TbPaperclip size={empty ? '1.5rem' : '1.25rem'} />}
                    label={empty ? 'No Attachments' : 'Upload More Files'}
                />
            </Dragger>
        </div>
    );

    if (loading) {
        return <BaseSkeleton active round paragraph={{ rows: 16 }} style={{ margin: '0 1rem' }} />;
    }

    if (filterActive && files.length === 0) {
        return (
            <EmptyView
                description='No attachments match the current search and filter criteria.'
                height={height}
                icon={<TbPaperclip size='1.25rem' style={{ color: token.colorTextTertiary }} />}
                label='No Attachments Found'
            />
        );
    }

    return (
        <section style={{ display: 'flex', flexDirection: 'column', height, minHeight: 0 }}>
            {files.length ? (
                <>
                    <div
                        style={{
                            borderBottom: `1px solid ${token.colorBorder}`,
                            flex: '1 1 auto',
                            minHeight: 0,
                            overflow: 'hidden',
                            position: 'relative',
                        }}
                    >
                        <div
                            style={{
                                height: '100%',
                                overflowY: 'auto',
                                paddingBottom: bulkActions ? '4rem' : undefined,
                            }}
                        >
                            {renderAttachmentTree()}
                        </div>
                        {bulkActions ? (
                            <Flex
                                align='center'
                                justify='center'
                                style={{
                                    bottom: '0.75rem',
                                    left: 0,
                                    pointerEvents: 'none',
                                    position: 'absolute',
                                    right: 0,
                                    zIndex: 10,
                                }}
                            >
                                <div style={{ pointerEvents: 'auto' }}>{bulkActions}</div>
                            </Flex>
                        ) : null}
                    </div>
                    {canUpload && renderUploadControls()}
                </>
            ) : (
                <div style={{ flex: '1 1 auto', minHeight: 0 }}>
                    {canUpload ? (
                        renderUploadControls(true)
                    ) : (
                        <EmptyView
                            description={`You do not have permission to upload attachments to this ${resourceLabel.toLowerCase()}.`}
                            height='100%'
                            icon={<TbPaperclip size='1.25rem' style={{ color: token.colorTextTertiary }} />}
                            label='No Attachments'
                        />
                    )}
                </div>
            )}
            <AttachmentUploadQueue items={uploadItems} onCancel={cancel} onRetry={retry} />
        </section>
    );
};

export default AttachmentFileList;
